import React, { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { FaCamera, FaTimes, FaBarcode, FaQrcode } from 'react-icons/fa';

const OutboundBarcodeScanner = ({ onComplete, onClose, title = "扫描商品条码/二维码" }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [scanMode, setScanMode] = useState('barcode'); // 'barcode' 或 'qrcode'
  const videoRef = useRef(null);
  const codeReader = useRef(null);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      videoRef.current.srcObject = stream;
      
      codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, err) => {
          if (result) {
            handleCodeScanned(result.text);
          }
          if (err && !(err instanceof NotFoundException)) {
            console.error(err);
          }
        }
      );
    } catch (err) {
      setError('无法访问摄像头');
      console.error(err);
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const handleCodeScanned = (code) => {
    if (code && code.trim()) {
      onComplete(code.trim());
      stopScanning();
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleCodeScanned(manualInput.trim());
    }
  };

  const switchScanMode = (mode) => {
    setScanMode(mode);
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* 头部 */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* 扫描模式切换 */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
            <button
              onClick={() => switchScanMode('barcode')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                scanMode === 'barcode'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <FaBarcode className="mr-2" />
              条码
            </button>
            <button
              onClick={() => switchScanMode('qrcode')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                scanMode === 'qrcode'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <FaQrcode className="mr-2" />
              二维码
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-red-800">
              {error}
            </div>
          )}

          {/* 扫描区域 */}
          <div className="space-y-4">
            {scanning ? (
              <div className="relative">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  className="w-full h-64 bg-black rounded-lg"
                />
                <button
                  onClick={stopScanning}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                >
                  <FaTimes />
                </button>
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                  正在扫描{scanMode === 'barcode' ? '条码' : '二维码'}
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                {scanMode === 'barcode' ? (
                  <FaBarcode className="text-4xl text-blue-400 mx-auto mb-2" />
                ) : (
                  <FaQrcode className="text-4xl text-green-400 mx-auto mb-2" />
                )}
                <p className="text-gray-600 mb-4">
                  点击开始扫描{scanMode === 'barcode' ? '条码' : '二维码'}
                </p>
                <button
                  onClick={startScanning}
                  className={`${
                    scanMode === 'barcode' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  } text-white px-6 py-2 rounded-lg flex items-center mx-auto`}
                >
                  <FaCamera className="mr-2" />
                  开始扫描
                </button>
              </div>
            )}

            {/* 手动输入 */}
            <form onSubmit={handleManualSubmit} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                或手动输入{scanMode === 'barcode' ? '条码' : '二维码'}：
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  className="flex-1 p-2 border rounded-lg"
                  placeholder={`输入${scanMode === 'barcode' ? '条码' : '二维码'}...`}
                />
                <button
                  type="submit"
                  className={`${
                    scanMode === 'barcode' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  } text-white px-4 py-2 rounded-lg`}
                >
                  确认
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutboundBarcodeScanner;